import { useDispatch, useSelector } from "react-redux";

import { CirclePlusIcon, MakerIcon } from "@/assets/icons";
import EmptyPlaceholder from "@/components/emptyPlaceholder";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { MAX_POI } from "@/constants/dataset";
import { useMap } from "@/hooks/map/core/useMapContext";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { RootState } from "@/store";
import { setIsShowMarkerLabel } from "@/store/slice/spatialSlice";
import { toggleDatasetExplorerPopup } from "@/store/slice/uiSlice";
import { CatchmentDatasetConfig } from "@/types/spatial";

import CatchmentDatasetList from "./catchmentDatasetList";

interface Props {
  onOpenDatasetTabularModal?: () => void;
}
export function countTotalRows(datasets?: CatchmentDatasetConfig["datasets"]) {
  if (!datasets) return 0;

  let totalRows = 0;

  for (const key of Object.keys(datasets)) {
    const rows = datasets[key]?.rawDataset?.rows;
    if (Array.isArray(rows)) {
      totalRows += rows.length; // Intentionally summing all rows.length across datasets, not just assigning one value
    }
  }

  return totalRows;
}

const CatchmentDataset = ({ onOpenDatasetTabularModal }: Props) => {
  const dispatch = useDispatch();
  const { hideAllMarkerLabels, showAllMarkerLabels } = useMap();
  const { catchmentSettingsTemp, isShowMarkerLabel } = useSelector(
    (state: RootState) => state.spatial
  );

  const { isNewUIDatasetConfigEnabled } = useAppFeatures();

  const handleChangeShowLabel = (checked: boolean, datasetId?: string) => {
    dispatch(setIsShowMarkerLabel({ checked, datasetId }));
    if (checked) {
      showAllMarkerLabels();
    } else {
      hideAllMarkerLabels();
    }
  };

  return (
    <div className="flex flex-col">
      {catchmentSettingsTemp?.datasetConfig?.visualizationOrder.length ===
        0 && (
        <Button
          variant="outline"
          className="rounded-xl h-[32px] font-bold btn-secondary mt-2"
          onClick={() => {
            dispatch(toggleDatasetExplorerPopup(true));
          }}
        >
          <CirclePlusIcon />
          <span>Add Dataset</span>
        </Button>
      )}
      <CatchmentDatasetList
        onOpenDatasetTabularModal={onOpenDatasetTabularModal}
        handleChangeShowLabel={handleChangeShowLabel}
      />
      <div className="flex flex-row items-center mt-2">
        <p className="text-[10px] mt-2">
          List of marker{" "}
          <span className="text-teal-500 font-bold">
            {countTotalRows(catchmentSettingsTemp?.datasetConfig?.datasets)}/
            {MAX_POI}
          </span>
        </p>
        {!isNewUIDatasetConfigEnabled && (
          <div className="flex flex-row items-center ml-auto">
            <span className="text-[10px] text-txtHeading mr-2">Show label</span>
            <Switch
              checked={isShowMarkerLabel}
              onCheckedChange={(checked) => handleChangeShowLabel(checked)}
            />
          </div>
        )}
      </div>
      {catchmentSettingsTemp?.datasetConfig?.visualizationOrder.length ===
        0 && (
        <EmptyPlaceholder
          className="my-2 bg-white px-4"
          icon={
            <MakerIcon className="fill-light-gray" width={21} height={26} />
          }
          description="Add a catchment or upload a dataset. Only one method can be used at a time."
        />
      )}
    </div>
  );
};

export default CatchmentDataset;
